/* eslint-disable max-lines */
import { WealthGetDataModel } from '@insurely/common-api-client';
import { build, perBuild } from '@jackfranklin/test-data-bot';

import { swedishFaker } from '@main/mocks/fake-utils/collections/se/swedishFaker';
import {
  AvailableFinancialProductCompanies,
  Classification,
  Currency,
  FinancialProductType,
  HoldingType,
} from '@main/types';

const investmentSpecificData = {
  accountInformation: {
    cashAccountNumber: String(
      swedishFaker.number.float({ min: 1000000, max: 9999999, fractionDigits: 0 }),
    ),
    cashValue: {
      amount: 4514.27,
      currency: Currency.SEK,
    },
    interestRate: swedishFaker.number.float({ min: -0.1, max: 2, fractionDigits: 4 }),
  },
  monthlySavingsAmount: {
    amount: 4000,
    currency: Currency.SEK,
  },
};

const kfSpecificData = {
  accountInformation: undefined,
  monthlySavingsAmount: undefined,
  fees: {
    feesCollected: false,
    feesPreviousYear: {
      amount: 519.15,
      currency: Currency.SEK,
    },
  },
  insurance: {
    survivorsProtection: true,
  },
  traditionallyManaged: false,
};

const removeInvestmentSpecificData = {
  accountInformation: undefined,
  monthlySavingsAmount: undefined,
};

const pensionOverrides = {
  holder: {},
  holdings: [
    {
      feeVariable: 0.0025,
      isin: 'SE0000810772',
      name: 'Skandia Europa Exponering',
      quantity: 89.651,
      type: HoldingType.FUND,
      value: {
        amount: 66124,
        currency: Currency.SEK,
      },
    },
  ],
  financialInformation: {
    currentValue: {
      amount: swedishFaker.number.float({ min: 1000, max: 190000, fractionDigits: 2 }),
      currency: Currency.SEK,
    },
    totalDeposits: {
      amount: swedishFaker.number.float({ min: 1000, max: 190000, fractionDigits: 2 }),
      currency: Currency.SEK,
    },
  },
};

const pensionSpecificData = {
  activePayment: false,
  activePayout: false,
  fees: {
    feeVariable: 0,
    feesCollected: false,
    feesPreviousYear: {
      amount: 120,
      currency: Currency.SEK,
    },
  },
  insurance: {
    insuredPersonName: 'Jens Nygaard',
    survivorsProtection: false,
  },
  movability: {
    evaluatedRules: [
      {
        result: 'MOVABLE',
        rule: 'OLD_TRAD_PENSION',
      },
      {
        result: 'MOVABLE',
        rule: 'ACTIVE_PAYOUT',
      },
      {
        result: 'MOVABLE',
        rule: 'ACTIVE_PAYMENT',
      },
    ],
    result: 'MOVABLE',
  },
  productNumber: 'S123456789',
  startDate: '2016-04-01',
  traditionallyManaged: false,
};

const ocuupationalPensionSpecificData = {
  holder: {
    name: 'Company AB',
    organization: {
      city: 'Stockholm',
      financialInformation: {
        employeeCount: 333,
        financialPeriod: {
          endDate: '2022-12-31',
          startDate: '2022-01-01',
        },
        netIncome: {
          amount: 604300000,
          currency: Currency.SEK,
        },
        shareCapital: {
          amount: 100000000,
          currency: Currency.SEK,
        },
        turnover: {
          amount: 2211907000,
          currency: Currency.SEK,
        },
      },
      name: 'Company AB',
      number: '5563141234',
    },
  },
};

export const fakeNewSwedishWealthAccount = build<WealthGetDataModel>({
  fields: {
    id: perBuild(() => swedishFaker.string.uuid()),
    company: AvailableFinancialProductCompanies.SE_DEMO,
    companyDisplayName: 'Demo Company',
    financialInformation: {
      currentValue: {
        amount: swedishFaker.number.float({ min: 1000, max: 190000, fractionDigits: 2 }),
        currency: Currency.SEK,
      },
      performanceSinceStart: swedishFaker.number.float({ min: -0.1, max: 2, fractionDigits: 4 }),
    },
    holder: {
      name: 'Jens Nygaard',
    },
    holdings: [
      {
        acquisitionValue: {
          amount: 18883.51,
          currency: Currency.SEK,
        },
        feeVariable: 0,
        isin: 'SE0001718388',
        name: 'Avanza Zero',
        quantity: 54.076858,
        type: HoldingType.FUND,
        value: {
          amount: 22468.39,
          currency: Currency.SEK,
        },
      },
    ],
    personalInformation: {
      PERSONAL_NUMBER: '900106-1234',
    },
    productName: 'My account name',
    productNumber: String(
      swedishFaker.number.float({ min: 1000000, max: 9999999, fractionDigits: 0 }),
    ),
    type: FinancialProductType.UNKNOWN_INVESTMENT_TYPE,
    ...investmentSpecificData,
  },
  traits: {
    investment: {
      overrides: {
        accountInformation: {
          cashAccountNumber: '1234567',
          cashValue: {
            amount: 4514.27,
            currency: Currency.SEK,
          },
          interestRate: 0.0025,
        },
        monthlySavingsAmount: {
          amount: 4000,
          currency: Currency.SEK,
        },
      },
    },
    custodyAccount: {
      overrides: {
        productName: 'Min Depå',
        type: FinancialProductType.CUSTODY_ACCOUNT,
      },
    },
    iskAccount: {
      overrides: {
        productName: 'Min ISK',
        type: FinancialProductType.ISK,
      },
    },
    kfAccount: {
      overrides: {
        ...removeInvestmentSpecificData,
        ...kfSpecificData,
        type: FinancialProductType.KF,
        productName: 'Fondspar kapitalförsäkring',
      },
    },
    pension: {
      overrides: {
        ...removeInvestmentSpecificData,
        ...pensionOverrides,
        ...pensionSpecificData,
        type: FinancialProductType.UNKNOWN_PENSION_TYPE,
        productName: 'Pension',
      },
    },
    ipsPensionAccount: {
      overrides: {
        type: FinancialProductType.IPS_PENSION,
        productName: 'IPS Pension',
      },
    },
    privatePensionAccount: {
      overrides: {
        type: FinancialProductType.PRIVATE_PENSION,
        productName: 'Private Pension',
      },
    },
    occupationalPensionAccount: {
      overrides: {
        traditionallyManaged: true,
        ...ocuupationalPensionSpecificData,
        type: FinancialProductType.OCCUPATIONAL_PENSION,
        productName: 'Tjänstepension',
      },
    },
    collectiveOccupationalPensionAccount: {
      overrides: {
        type: FinancialProductType.COLLECTIVE_OCCUPATIONAL_PENSION,
        classification: Classification.ITP,
        ...ocuupationalPensionSpecificData,
        productName: 'ITP 1, Traditionell försäkring',
      },
    },
  },
});

export const getFakeNewSwedishWealthAccounts = () => [
  fakeNewSwedishWealthAccount.one({
    traits: ['investment'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['investment', 'custodyAccount'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['investment', 'iskAccount'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['kfAccount'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['pension'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['pension', 'ipsPensionAccount'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['pension', 'privatePensionAccount'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['pension', 'occupationalPensionAccount'],
  }),
  fakeNewSwedishWealthAccount.one({
    traits: ['pension', 'collectiveOccupationalPensionAccount'],
  }),
];
